import React, { useEffect, useRef, useState } from 'react';
import { itemFilesAPI } from '@/lib/api';
import { FileText, FileImage, Box, File as FileIcon, Loader2 } from 'lucide-react';

const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.tif', '.tiff'];
const CAD_EXTS = ['.dwg', '.dxf', '.step', '.stp', '.iges', '.igs', '.stl', '.sldprt', '.sldasm',
  '.slddrw', '.ipt', '.iam', '.idw', '.catpart', '.catproduct', '.prt', '.asm', '.x_t', '.x_b',
  '.3mf', '.obj', '.sat', '.jt', '.3dm', '.f3d'];

const extOf = (name = '') => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
};

export const isImageFile = (file) => {
  if (!file) return false;
  const ct = (file.content_type || '').toLowerCase();
  if (ct.startsWith('image/')) return true;
  return IMAGE_EXTS.includes(extOf(file.original_name));
};

const kind = (file) => {
  const ext = extOf(file?.original_name);
  if (isImageFile(file)) return 'image';
  if (ext === '.pdf' || (file?.content_type || '').includes('pdf')) return 'pdf';
  if (CAD_EXTS.includes(ext)) return 'cad';
  return 'file';
};

const SIZE_MAP = { sm: 'w-10 h-10', md: 'w-16 h-16', lg: 'w-24 h-24' };

/**
 * Renders a thumbnail for an item attachment.
 * - Image files: fetches the blob (auth) and shows a real thumbnail.
 * - PDF / CAD / other: shows a labelled file-type icon.
 */
export function AttachmentThumb({ itemId, file, size = 'md', onClick, className = '' }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const objectUrlRef = useRef('');
  const box = SIZE_MAP[size] || SIZE_MAP.md;
  const type = kind(file);

  useEffect(() => {
    let cancelled = false;
    if (type !== 'image') return undefined;
    setLoading(true);
    itemFilesAPI.download(itemId, file.id)
      .then(res => {
        if (cancelled) return;
        const objUrl = window.URL.createObjectURL(res.data);
        objectUrlRef.current = objUrl;
        setUrl(objUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (objectUrlRef.current) window.URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [itemId, file?.id, type]);

  const ext = extOf(file?.original_name).replace('.', '').toUpperCase();
  const IconCmp = type === 'pdf' ? FileText : type === 'cad' ? Box : type === 'image' ? FileImage : FileIcon;
  const iconColor = type === 'pdf' ? 'text-red-500' : type === 'cad' ? 'text-indigo-500' : 'text-slate-400';

  return (
    <button
      type="button"
      onClick={onClick}
      title={file?.original_name}
      data-testid={`attachment-thumb-${file?.id}`}
      className={`relative ${box} rounded-md border bg-muted/40 overflow-hidden flex flex-col items-center justify-center hover:ring-2 hover:ring-amber-400 transition ${className}`}
    >
      {type === 'image' && url && !failed ? (
        <img src={url} alt={file?.original_name} className="w-full h-full object-cover" />
      ) : type === 'image' && loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      ) : (
        <>
          <IconCmp className={`w-5 h-5 ${iconColor}`} />
          {ext && <span className="mt-0.5 text-[8px] font-semibold text-muted-foreground leading-none">{ext}</span>}
        </>
      )}
    </button>
  );
}

/**
 * Compact thumbnail for a list row, driven by the item's `primary_attachment`
 * summary returned by the items list endpoint (no per-row list fetch).
 */
export function ItemRowThumb({ itemId, primaryAttachment, count = 0, onClick }) {
  if (!primaryAttachment) {
    return <div className="w-9 h-9 rounded-md border border-dashed bg-muted/30 flex items-center justify-center flex-shrink-0" title="No attachments"><FileIcon className="w-3.5 h-3.5 text-muted-foreground/40" /></div>;
  }
  return (
    <div className="relative flex-shrink-0">
      <AttachmentThumb itemId={itemId} file={primaryAttachment} size="sm" onClick={onClick} className="w-9 h-9" />
      {count > 1 && (
        <span className="absolute -top-1.5 -right-1.5 bg-amber-600 text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid={`attachment-count-${itemId}`}>{count}</span>
      )}
    </div>
  );
}

/**
 * Fetches and renders a compact row of thumbnails for an item's attachments.
 * Used in list/approval views where files aren't already loaded.
 */
export function ItemAttachmentsPreview({ itemId, size = 'sm', max = 4, emptyText = '—', onOpen }) {
  const [files, setFiles] = useState(null);

  useEffect(() => {
    let cancelled = false;
    itemFilesAPI.list(itemId)
      .then(res => { if (!cancelled) setFiles(res.data || []); })
      .catch(() => { if (!cancelled) setFiles([]); });
    return () => { cancelled = true; };
  }, [itemId]);

  const handleOpen = async (file) => {
    if (onOpen) return onOpen(file);
    try {
      const res = await itemFilesAPI.download(itemId, file.id);
      const objUrl = window.URL.createObjectURL(res.data);
      window.open(objUrl, '_blank');
    } catch { /* ignore */ }
  };

  if (files === null) return <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />;
  if (files.length === 0) return <span className="text-[11px] text-muted-foreground">{emptyText}</span>;

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`attachments-preview-${itemId}`}>
      {files.slice(0, max).map(f => (
        <AttachmentThumb key={f.id} itemId={itemId} file={f} size={size} onClick={() => handleOpen(f)} />
      ))}
      {files.length > max && (
        <span className="text-[10px] text-muted-foreground">+{files.length - max}</span>
      )}
    </div>
  );
}
